/**
 * watch-battery/index.js — the page of /watch-battery-finder.
 *
 * The search itself is ./search.js (pure, tested). This file draws rows, the
 * "nothing found" state and the suggest form.
 *
 * Queue triage 2026-10-15, three things this file now does differently:
 *   - the "nothing found" state is a next step, not a shrug: the brand's
 *     typical rows (they used to vanish the moment a model was typed) and
 *     where each maker prints the code;
 *   - search telemetry carries the query the person SETTLED on. It used to
 *     log once per page, i.e. the first two characters typed ("36", "lq"),
 *     which made every "no result" unreadable;
 *   - the suggest form opens pre-filled from the search, and opens again
 *     after a send. It used to stay dead after the first one: the toggle
 *     focused a hidden input, and people rage-clicked it (4× on an iPhone).
 *
 * Queue 2026-10-28: a missing caliber one look-alike character away from a
 * code of the table ("7192" → 7T92, "BT63" → 8T63) is offered as a button
 * under the miss — the person compares it with the engraving.
 */
import { buildIndex, search, brandTypical, compact } from './search.js';

const CFG = window.__WB || {};
const DB = Array.isArray(CFG.db) ? CFG.db : [];
const STR = CFG.str || {};
const SLUG = 'watch-battery-finder';
const IDX = buildIndex(DB);

const $ = (id) => document.getElementById(id);
const brandSel = $('wb-brand');
const qIn = $('wb-q');
const hits = $('wb-hits');
const empty = $('wb-empty');
const countEl = $('wb-count');
const miss = $('wb-miss');
const missCal = $('wb-miss-cal');
const missAlt = $('wb-miss-alt');
const missTyp = $('wb-miss-typ');
const missTypH = $('wb-miss-typ-h');
const missTypList = $('wb-miss-typ-list');

const ae = (data) => { try { if (window.__ae) window.__ae('tool_interact', Object.assign({ slug: SLUG }, data)); } catch (e) {} };

function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function rowHtml(r) {
    let note = '';
    if (r.battery.indexOf('AUTOMATIC') === 0) note = ' <span class="wb-hit-note">— ' + esc(STR.automatic || '') + '</span>';
    else if (/solar|rechargeable/i.test(r.battery)) note = ' <span class="wb-hit-note">— ' + esc(STR.solar || '') + '</span>';
    return '<div class="wb-hit">'
        + '<div class="wb-hit-name">' + esc(r.brand) + ' <span class="wb-hit-model">' + esc(r.model) + '</span></div>'
        + '<div class="wb-hit-mod">' + (r.module && r.module !== '—' ? esc(STR.module || '') + ' ' + esc(r.module) : '') + '</div>'
        + '<div class="wb-hit-bat">' + esc(r.battery) + note + '</div>'
        + '</div>';
}

function highlightTable(rows, q) {
    document.querySelectorAll('#wb-table tr.wb-hl').forEach((tr) => tr.classList.remove('wb-hl'));
    if (q.length < 2 || !rows.length || rows.length === DB.length) return;
    const codes = new Set(rows.map((r) => r.battery.split(' (')[0].split(' or ')[0].trim()));
    document.querySelectorAll('#wb-table tbody tr').forEach((tr) => {
        const bat = tr.getAttribute('data-bat');
        codes.forEach((code) => { if (bat && (bat.indexOf(code) !== -1 || code.indexOf(bat) !== -1)) tr.classList.add('wb-hl'); });
    });
}

/* ── telemetry of the settled query ──────────────────────────────────── */
const TRACK_MS = 900;
const TRACK_CAP = 8;
const tracked = new Set();
let trackTimer = 0;
let last = { q: '', res: null };

function trackSettled() {
    const q = last.q;
    const res = last.res;
    if (!res || q.length < 2 || tracked.has(q) || tracked.size >= TRACK_CAP) return;
    tracked.add(q);
    const data = { a: res.rows.length ? 'search' : 'search_no_result', q: q.slice(0, 60), n: res.rows.length, pass: res.pass };
    if (!res.rows.length) data.typ = brandTypical(IDX, res.brand).length;
    if (!res.rows.length && res.alt && res.alt.length) data.alt = res.alt.length;
    if (res.brand) data.brand = res.brand;
    ae(data);
}

/* ── render ──────────────────────────────────────────────────────────── */
function render() {
    const brand = brandSel ? brandSel.value : '';
    const q = qIn ? qIn.value.trim() : '';
    const res = search(IDX, q, { brand });
    last = { q, res };
    const rows = res.rows;
    highlightTable(rows, q);

    clearTimeout(trackTimer);
    trackTimer = setTimeout(trackSettled, TRACK_MS);

    if (!rows.length) {
        hits.innerHTML = '';
        countEl.hidden = true;
        empty.hidden = false;
        renderMiss(res);
        return;
    }
    empty.hidden = true;
    if (miss) miss.hidden = true;
    if (q || brand) {
        countEl.textContent = String(STR.found || ':count').replace(':count', rows.length);
        countEl.hidden = false;
    } else {
        countEl.hidden = true;
    }
    hits.innerHTML = rows.slice(0, 50).map(rowHtml).join('');
}

/* "7192" on a caseback that reads 7T92: the table's codes one look-alike
   character away, offered as buttons — the person compares with the
   engraving; the page never answers for a code nobody typed. */
function renderAlt(res) {
    if (!missAlt) return;
    const alt = (res.alt || []).slice(0, 3).map((a) => a.toUpperCase());
    missAlt.hidden = !alt.length;
    if (!alt.length) { missAlt.innerHTML = ''; return; }
    const btns = alt.map((a) => '<button type="button" class="wb-alt" data-alt="' + esc(a) + '">' + esc(a) + '</button>').join(' ');
    const parts = String(STR.lookalike || ':code').split(':code');
    missAlt.innerHTML = esc(parts[0]) + btns + esc(parts.slice(1).join(''));
}

if (missAlt) {
    missAlt.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-alt]');
        if (!b || !qIn) return;
        ae({ a: 'lookalike_pick', q: last.q.slice(0, 60), alt: b.getAttribute('data-alt') });
        qIn.value = b.getAttribute('data-alt');
        render();
    });
}

function renderMiss(res) {
    if (!miss) return;
    renderAlt(res);
    const cal = res.caseback && res.unmatched.includes(res.caseback.caliber) ? res.caseback.caliber.toUpperCase() : '';
    missCal.hidden = !cal;
    if (cal) missCal.textContent = String(STR.caliber_missing || '').replace(':code', cal);
    const typical = brandTypical(IDX, res.brand);
    missTyp.hidden = !typical.length;
    if (typical.length) {
        missTypH.textContent = String(STR.typical || '').replace(':brand', res.brand);
        missTypList.innerHTML = typical.map(rowHtml).join('');
    } else {
        missTypList.innerHTML = '';
    }
    miss.hidden = false;
}

/* ── brand drop-down ─────────────────────────────────────────────────── */
if (brandSel) {
    [...new Set(DB.map((r) => r.brand))].filter((b) => b !== 'Generic').sort().forEach((b) => {
        const opt = document.createElement('option');
        opt.value = b;
        opt.textContent = b;
        brandSel.appendChild(opt);
    });
    brandSel.addEventListener('change', () => {
        render();
        ae({ a: 'brand_filter', brand: brandSel.value || 'all' });
    });
}
if (qIn) qIn.addEventListener('input', render);
render();

/* ── suggest form ────────────────────────────────────────────────────── */
const suggestBtn = $('wb-suggest-btn');
const suggestCard = $('wb-suggest-card');
const suggestForm = $('wb-suggest-form');
const suggestOk = $('wb-suggest-ok');
const fBrand = $('wb-sg-brand');
const fModel = $('wb-sg-model');
const fModule = $('wb-sg-module');
const fBattery = $('wb-sg-battery');
const fEmail = $('wb-sg-email');
const fSubmit = $('wb-sg-submit');

/* What the person already typed into the search is what they would type
   here: brand from the drop-down or a recognised brand word, the rest is the
   model, a caseback caliber goes to "module". Only empty fields are filled. */
function prefill() {
    const q = last.q;
    const res = last.res || { tokens: [], brand: null, caseback: null };
    let brand = (brandSel && brandSel.value) || res.brand || '';
    const brandKey = compact(brand.split(/[\s/]+/)[0] || '');
    // A word stays in "model" when it survived as a search token (filler,
    // the brand itself and its misspelling did not) or is a caseback code.
    let words = q.split(/[\s,;]+/).filter(Boolean).filter((w) => {
        const c = compact(w);
        return c !== brandKey && (res.tokens.includes(c) || /^[0-9a-z]{4}-[0-9a-z]{4}$/i.test(w));
    });
    if (!brand && words.length >= 2 && !/\d/.test(words[0])) brand = words.shift();
    if (fBrand && !fBrand.value) fBrand.value = brand;
    if (fModel && !fModel.value) fModel.value = words.join(' ').slice(0, 200);
    if (fModule && !fModule.value && res.caseback) fModule.value = res.caseback.caliber.toUpperCase();
}

if (suggestBtn && suggestCard) {
    suggestBtn.addEventListener('click', () => {
        // After a send the form is replaced by the thank-you; a second click
        // asks for another watch, so it gets a fresh form (e-mail kept).
        if (suggestOk && !suggestOk.hidden) {
            const email = fEmail ? fEmail.value : '';
            if (suggestForm) { suggestForm.reset(); suggestForm.hidden = false; }
            if (fEmail) fEmail.value = email;
            suggestOk.hidden = true;
            if (fSubmit) fSubmit.disabled = false;
        }
        suggestCard.hidden = false;
        prefill();
        const first = fBrand && !fBrand.value ? fBrand : (fModel && !fModel.value ? fModel : fBrand);
        if (first) {
            first.focus({ preventScroll: true });
            first.scrollIntoView({ block: 'nearest' });
        }
        ae({ a: 'suggest_open', q: last.q.slice(0, 60), brand: (last.res && last.res.brand) || '' });
    });
}

if (suggestForm) {
    suggestForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const brand = fBrand.value.trim();
        const model = fModel.value.trim();
        if (!brand || !model) return;
        fSubmit.disabled = true;
        const payload = {
            tool_slug: SLUG,
            data: { brand, model, module: fModule.value.trim(), battery: fBattery.value.trim() },
            email: fEmail.value.trim(),
            locale: CFG.locale || window.__LOCALE || 'en',
        };
        const send = (token) => fetch('/api/tool-suggestion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify(payload),
        });
        const csrf = (document.querySelector('meta[name="csrf-token"]') || {}).content || '';
        send(csrf).then((r) => {
            if (r.status === 419 && window.__refreshCsrf) return window.__refreshCsrf().then(send);
            return r;
        }).then((r) => {
            if (r.ok) {
                suggestForm.hidden = true;
                suggestOk.hidden = false;
                ae({ a: 'suggest_submit', brand, q: last.q.slice(0, 60) });
            } else {
                fSubmit.disabled = false;
                if (window.__toast) window.__toast(STR.ui_error || '', 'error');
            }
        }).catch(() => {
            fSubmit.disabled = false;
            if (window.__toast) window.__toast(STR.net_error || '', 'error');
        });
    });
}
